/**
 * Centralized route configuration for the CRM application.
 * Single source of truth for paths, permissions, and route definitions.
 */
import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "../components/ProtectedRoute";
import Login from "../components/Login/Login";
import Register from "../components/Register";
import Dashboard from "../pages/Dashboard.jsx";
import CreateMerchant from "../pages/CreateMerchant";
import InUnderwriting from "../pages/InUnderwriting";
import Pending from "../pages/Pending";
import Complete from "../pages/Complete";
import ViewMerchant from "../pages/ViewMerchant";
import ProfilePage from "../pages/ProfilePage.jsx";
import UserCreatePage from "../pages/UserCreatePage.jsx";
import UserListPage from "../pages/UserListPage.jsx";
import CustomerCreatePage from "../pages/CustomerCreatePage.jsx";
import CustomerListPage from "../pages/CustomerListPage.jsx";
import TeamsListPage from "../pages/TeamsListPage.jsx";
import CreateTeamPage from "../pages/CreateTeamPage.jsx";
import ViewLogsPage from "../pages/ViewLogsPage.jsx";
import LeadList from "../pages/Leads/LeadList.jsx";
import CreateLead from "../pages/Leads/CreateLead.jsx";
import MeetingCalendar from "../pages/meetings/MeetingCalendar.jsx";
import MeetingView from "../pages/meetings/MeetingView.jsx";
import CreateMeeting from "../pages/meetings/CreateMeeting.jsx";
import EditMeeting from "../pages/meetings/EditMeeting.jsx";
import SalesDbaListing from "../pages/SalesDbaListing.jsx";
import LeadDetail from "../pages/Leads/LeadDetail.jsx";
import PermissionAssignment from "../pages/permission/PermissionAssignment.jsx";
import RoleList from "../pages/roles/RoleList.jsx";
import CreateRole from "../pages/roles/CreateRole.jsx";
import IssueList from "../pages/Issues/IssueList.jsx";
import IssueDetail from "../pages/Issues/IssueDetail.jsx";
import MerchantList from "../pages/Merchants/MerchantsList.jsx";
import Equipemnts from "../pages/Merchants/Equipemnts.jsx";
import ContractList from "../pages/contracts/ContractList.jsx";
import DailySummaryList from "../pages/dailySummary/DailySummaryList.jsx";
import CreateDailySummary from "../pages/dailySummary/CreateDailySummary.jsx";
import EditDailySummary from "../pages/dailySummary/EditDailySummary.jsx";
import ViewDailySummary from "../pages/dailySummary/ViewDailySummary.jsx";
import CallsCounterSummary from "../pages/CallsCounterSummary.jsx";
import LeadsReport from "../pages/LeadsReport.jsx";
import RecentActivitiesReport from "../pages/reports/RecentActivitiesReport.jsx";
import GenerateSalesInformation from "../pages/reports/GenerateSalesInformation.jsx";
import GeneratePaymentSummary from "../pages/reports/GeneratePaymentSummary.jsx";
import ClientsAsPerStatus from "../pages/reports/ClientsAsPerStatus.jsx";
import NotificationsPage from "../pages/NotificationsPage.jsx";
import CallRecordList from "../pages/callRecords/CallRecordList.jsx";
import CreateCallRecord from "../pages/callRecords/CreateCallRecord.jsx";
import ViewCallRecord from "../pages/callRecords/ViewCallRecord.jsx";
import EditCallRecord from "../pages/callRecords/EditCallRecord.jsx";
import InvoiceList from "../pages/invoices/InvoiceList.jsx";
import CreateInvoice from "../pages/invoices/CreateInvoice.jsx";
import ViewInvoice from "../pages/invoices/ViewInvoice.jsx";
import PaymentsList from "../pages/payments/PaymentsList.jsx";
import ViewPayment from "../pages/payments/ViewPayment.jsx";
import BrandList from "../pages/brands/BrandList.jsx";
import CreateBrand from "../pages/brands/CreateBrand.jsx";
import ViewBrand from "../pages/brands/ViewBrand.jsx";
import ServicesList from "../pages/services/ServicesList.jsx";
import CreateService from "../pages/services/CreateService.jsx";
import ViewService from "../pages/services/ViewService.jsx";
import LeadQuizList from "../pages/leadQuizzes/LeadQuizList.jsx";
import CreateLeadQuiz from "../pages/leadQuizzes/CreateLeadQuiz.jsx";
import ViewLeadQuiz from "../pages/leadQuizzes/ViewLeadQuiz.jsx";
import CompanyList from "../pages/companies/CompanyList.jsx";
import CreateCompany from "../pages/companies/CreateCompany.jsx";
import ViewCompany from "../pages/companies/ViewCompany.jsx";
import TaskTypeList from "../pages/taskTypes/TaskTypeList.jsx";
import CreateTaskType from "../pages/taskTypes/CreateTaskType.jsx";
import ViewTaskType from "../pages/taskTypes/ViewTaskType.jsx";
import TaskCategoryList from "../pages/taskCategories/TaskCategoryList.jsx";
import CreateTaskCategory from "../pages/taskCategories/CreateTaskCategory.jsx";
import ViewTaskCategory from "../pages/taskCategories/ViewTaskCategory.jsx";
import TaskTemplateList from "../pages/taskTemplates/TaskTemplateList.jsx";
import CreateTaskTemplate from "../pages/taskTemplates/CreateTaskTemplate.jsx";
import ViewTaskTemplate from "../pages/taskTemplates/ViewTaskTemplate.jsx";
import TaskList from "../pages/tasks/TaskList.jsx";
import CreateTask from "../pages/tasks/CreateTask.jsx";
import ViewTask from "../pages/tasks/ViewTask.jsx";
import PaymentErrorList from "../pages/paymentErrors/PaymentErrorList.jsx";
import ViewPaymentError from "../pages/paymentErrors/ViewPaymentError.jsx";
import { LIST_ROUTE_ANY_PERMISSIONS } from "./resourceListPermissions";
import PaymentAccounts from "../pages/paymentAccounts/index.jsx";
import AddPaymentAccounts from "../pages/paymentAccounts/AddPaymentAccounts.jsx";
import {
  ORDER_CREATE_PATH,
  ORDER_LIST_PATH,
  ORDER_VIEW_PATH,
  PAYMENT_ACCOUNTS_CREATE_PATH,
  PAYMENT_ACCOUNTS_EDIT_PATH,
  PAYMENT_ACCOUNTS_LIST_PATH,
  PAYMENT_ACCOUNTS_VIEW_PATH,
  SERVICE_PLANS_CREATE_PATH,
  SERVICE_PLANS_EDIT_PATH,
  SERVICE_PLANS_LIST_PATH,
  SERVICE_PLANS_VIEW_PATH,
  SERVICE_STATE_PRICES_CREATE_PATH,
  SERVICE_STATE_PRICES_EDIT_PATH,
  SERVICE_STATE_PRICES_LIST_PATH,
  SERVICE_STATE_PRICES_VIEW_PATH,
} from "./path.jsx";
import PaymentAccountDetails from "../pages/paymentAccounts/PaymentAccountDetails.jsx";
import CreateOrder from "../pages/orders/CreateOrder.jsx";
import OrderList from "../pages/orders/index.jsx";
import OrderDetails from "../pages/orders/OrderDetails.jsx";
import ServicePlans from "../pages/servicePlans/index.jsx";
import CreateServicePlan from "../pages/servicePlans/CreateServicePlan.jsx";
import ViewServicePlan from "../pages/servicePlans/ViewServicePlan.jsx";
import ServiceStatePrices from "../pages/serviceStatePrice/index.jsx";
import CreateServiceStatePrice from "../pages/serviceStatePrice/CreateServiceStatePrice.jsx";
import ViewServiceStatePrice from "../pages/serviceStatePrice/ViewServiceStatePrice.jsx";

/** Wraps a component with ProtectedRoute */
const Protected = ({ children, permission, permissions = [] }) => (
  <ProtectedRoute
    requiredPermission={permissions.length > 0 ? undefined : permission}
    requiredPermissions={permissions.length > 0 ? permissions : []}
  >
    {children}
  </ProtectedRoute>
);

/**
 * All application routes. Add new routes here to keep App.js lean.
 */
export const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    <Route
      path="/"
      element={
        <Protected>
          <Navigate to="/dashboard" replace />
        </Protected>
      }
    />

    <Route
      path="/dashboard"
      element={
        <Protected>
          <Dashboard />
        </Protected>
      }
    />
    <Route
      path="/profile"
      element={
        <Protected>
          <ProfilePage />
        </Protected>
      }
    />
    <Route
      path="/notifications"
      element={
        <Protected>
          <NotificationsPage />
        </Protected>
      }
    />

    {/* Merchant / DBA routes */}
    <Route
      path="/create-merchant"
      element={
        <Protected permission="dba-create">
          <CreateMerchant />
        </Protected>
      }
    />
    <Route
      path="/in-underwriting"
      element={
        <Protected permission="dba-list">
          <InUnderwriting />
        </Protected>
      }
    />

    <Route
      path="/merchants/accounts"
      element={
        <Protected permission="dba-list">
          <MerchantList />
        </Protected>
      }
    />
    <Route
      path="/merchants/equipments"
      element={
        <Protected permission="dba-list">
          <Equipemnts />
        </Protected>
      }
    />

    <Route
      path="/pending"
      element={
        <Protected permission="dba-pending-list">
          <Pending />
        </Protected>
      }
    />
    <Route
      path="/complete"
      element={
        <Protected permission="dba-completed-list">
          <Complete />
        </Protected>
      }
    />
    <Route
      path="/view-merchant"
      element={
        <Protected permission="dba-view">
          <ViewMerchant />
        </Protected>
      }
    />
    <Route
      path="/sales-dba"
      element={
        <Protected permission="dba-sales-list">
          <SalesDbaListing />
        </Protected>
      }
    />

    {/* User management */}
    <Route
      path="/create-user"
      element={
        <Protected permissions={["user-create", "user-edit", "user-view"]}>
          <UserCreatePage />
        </Protected>
      }
    />
    <Route
      path="/users-list"
      element={
        <Protected permissions={["user-list", "user-direct-permission"]}>
          <UserListPage />
        </Protected>
      }
    />

    {/* Customer management */}
    <Route
      path="/create-customer"
      element={
        <Protected
          permissions={["customer-create", "customer-edit", "customer-view"]}
        >
          <CustomerCreatePage />
        </Protected>
      }
    />
    <Route
      path="/customers-list"
      element={
        <Protected permissions={["customer-list", "customer-list-front"]}>
          <CustomerListPage />
        </Protected>
      }
    />

    {/* Team management */}
    <Route
      path="/teams-lists"
      element={
        <Protected permission="team-list">
          <TeamsListPage />
        </Protected>
      }
    />
    <Route
      path="/create-team"
      element={
        <Protected permission="team-create">
          <CreateTeamPage />
        </Protected>
      }
    />

    {/* Leads */}
    <Route
      path="/leads"
      element={
        <Protected permission="lead-list">
          <LeadList />
        </Protected>
      }
    />
    <Route
      path="/leads/create"
      element={
        <Protected permission="lead-create">
          <CreateLead />
        </Protected>
      }
    />
    <Route
      path="/leads/edit/:id"
      element={
        <Protected permission="lead-edit">
          <CreateLead />
        </Protected>
      }
    />
    <Route
      path="/leads/view/:id"
      element={
        <Protected permission="lead-view">
          <LeadDetail />
        </Protected>
      }
    />

    {/* Call recordings */}
    <Route
      path="/call-records"
      element={
        <Protected permission="call-record-list">
          <CallRecordList />
        </Protected>
      }
    />
    <Route
      path="/call-records/create"
      element={
        <Protected permission="call-record-create">
          <CreateCallRecord />
        </Protected>
      }
    />
    <Route
      path="/call-records/view/:id"
      element={
        <Protected permission="call-record-view">
          <ViewCallRecord />
        </Protected>
      }
    />
    <Route
      path="/call-records/edit/:id"
      element={
        <Protected permission="call-record-edit">
          <EditCallRecord />
        </Protected>
      }
    />

    {/* Issues */}
    <Route
      path="/issues"
      element={
        <Protected permission="issue-list">
          <IssueList />
        </Protected>
      }
    />
    <Route
      path="/issues/view/:id"
      element={
        <Protected permission="issue-view">
          <IssueDetail />
        </Protected>
      }
    />

    {/* Roles */}
    <Route
      path="/roles"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/roles"]}>
          <RoleList />
        </Protected>
      }
    />
    <Route
      path="/roles/create"
      element={
        <Protected permission="role-create">
          <CreateRole />
        </Protected>
      }
    />
    <Route
      path="/roles/edit/:id"
      element={
        <Protected permission="role-edit">
          <CreateRole />
        </Protected>
      }
    />
    <Route path="/permission/role" element={<Navigate to="/roles" replace />} />
    <Route
      path="/permissions/assign"
      element={
        <Protected permissions={["role-view", "user-direct-permission"]}>
          <PermissionAssignment />
        </Protected>
      }
    />

    {/*  */}
    {/* Contracts */}
    <Route
      path="/contracts"
      element={
        <Protected permission="eSignContract-list">
          <ContractList />
        </Protected>
      }
    />

    {/* Invoices (static paths before /invoices) */}
    <Route
      path="/invoices/create"
      element={
        <Protected permission="invoice-create">
          <CreateInvoice />
        </Protected>
      }
    />
    <Route
      path="/invoices/edit/:id"
      element={
        <Protected permission="invoice-edit">
          <CreateInvoice />
        </Protected>
      }
    />
    <Route
      path="/invoices/view/:id"
      element={
        <Protected permission="invoice-view">
          <ViewInvoice />
        </Protected>
      }
    />
    <Route
      path="/invoices"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/invoices"]}>
          <InvoiceList />
        </Protected>
      }
    />

    {/* Payments */}
    <Route
      path="/payments/view/:id"
      element={
        <Protected permission="payment-view">
          <ViewPayment />
        </Protected>
      }
    />
    <Route
      path="/payments"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/payments"]}>
          <PaymentsList />
        </Protected>
      }
    />

    {/* Payment errors */}
    <Route
      path="/payment-errors/view/:id"
      element={
        <Protected permission="payment-error-view">
          <ViewPaymentError />
        </Protected>
      }
    />
    <Route
      path="/payment-errors"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/payment-errors"]}>
          <PaymentErrorList />
        </Protected>
      }
    />
    {/* Payments Account */}
    <Route
      path={PAYMENT_ACCOUNTS_LIST_PATH}
      element={
        <Protected
          permissions={LIST_ROUTE_ANY_PERMISSIONS["/payment-accounts"]}
        >
          <PaymentAccounts />
        </Protected>
      }
    />
    <Route
      path={PAYMENT_ACCOUNTS_CREATE_PATH}
      element={
        <Protected permission="payment-account-create">
          <AddPaymentAccounts />
        </Protected>
      }
    />
    <Route
      path={PAYMENT_ACCOUNTS_EDIT_PATH}
      element={
        <Protected permission="payment-account-edit">
          <AddPaymentAccounts />
        </Protected>
      }
    />
    <Route
      path={PAYMENT_ACCOUNTS_VIEW_PATH}
      element={
        <Protected permission="payment-account-view">
          <PaymentAccountDetails />
        </Protected>
      }
    />

    {/* Order */}

    <Route
      path={ORDER_LIST_PATH}
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/orders"]}>
          <OrderList />
        </Protected>
      }
    />
    <Route
      path={ORDER_CREATE_PATH}
      element={
        <Protected permission="order-create">
          <CreateOrder />
        </Protected>
      }
    />
    <Route
      path={ORDER_VIEW_PATH}
      element={
        <Protected permission="order-view">
          <OrderDetails />
        </Protected>
      }
    />

    {/* Brands */}
    <Route
      path="/brands/create"
      element={
        <Protected permission="brand-create">
          <CreateBrand />
        </Protected>
      }
    />
    <Route
      path="/brands/edit/:id"
      element={
        <Protected permission="brand-edit">
          <CreateBrand />
        </Protected>
      }
    />
    <Route
      path="/brands/view/:id"
      element={
        <Protected permission="brand-view">
          <ViewBrand />
        </Protected>
      }
    />
    <Route
      path="/brands"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/brands"]}>
          <BrandList />
        </Protected>
      }
    />

    {/* Services */}
    <Route
      path="/services/create"
      element={
        <Protected permission="service-create">
          <CreateService />
        </Protected>
      }
    />
    <Route
      path="/services/edit/:id"
      element={
        <Protected permission="service-edit">
          <CreateService />
        </Protected>
      }
    />
    <Route
      path="/services/view/:id"
      element={
        <Protected permission="service-view">
          <ViewService />
        </Protected>
      }
    />
    <Route
      path="/services"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/services"]}>
          <ServicesList />
        </Protected>
      }
    />

    {/* Service Plans */}
    <Route
      path={SERVICE_PLANS_LIST_PATH}
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/service-plans"]}>
          <ServicePlans />
        </Protected>
      }
    />
    <Route
      path={SERVICE_PLANS_CREATE_PATH}
      element={
        <Protected permission="service-plan-create">
          <CreateServicePlan />
        </Protected>
      }
    />
    <Route
      path={SERVICE_PLANS_EDIT_PATH}
      element={
        <Protected permission="service-plan-edit">
          <CreateServicePlan />
        </Protected>
      }
    />
    <Route
      path={SERVICE_PLANS_VIEW_PATH}
      element={
        <Protected permission="service-plan-view">
          <ViewServicePlan />
        </Protected>
      }
    />

    {/* Service State Price */}
    <Route
      path={SERVICE_STATE_PRICES_LIST_PATH}
      element={
        <Protected permission="service-state-price-list">
          <ServiceStatePrices />
        </Protected>
      }
    />
    <Route
      path={SERVICE_STATE_PRICES_CREATE_PATH}
      element={
        <Protected permission="service-state-price-create">
          <CreateServiceStatePrice />
        </Protected>
      }
    />
    <Route
      path={SERVICE_STATE_PRICES_VIEW_PATH}
      element={
        <Protected permission="service-state-price-view">
          <ViewServiceStatePrice />
        </Protected>
      }
    />
    <Route
      path={SERVICE_STATE_PRICES_EDIT_PATH}
      element={
        <Protected permission="service-state-price-edit">
          <CreateServiceStatePrice />
        </Protected>
      }
    />

    {/* Companies */}
    <Route
      path="/companies/create"
      element={
        <Protected permission="company-create">
          <CreateCompany />
        </Protected>
      }
    />
    <Route
      path="/companies/edit/:id"
      element={
        <Protected permission="company-edit">
          <CreateCompany />
        </Protected>
      }
    />
    <Route
      path="/companies/view/:id"
      element={
        <Protected permission="company-view">
          <ViewCompany />
        </Protected>
      }
    />
    <Route
      path="/companies"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/companies"]}>
          <CompanyList />
        </Protected>
      }
    />

    {/* Task types */}
    <Route
      path="/task-types/create"
      element={
        <Protected permission="task-type-create">
          <CreateTaskType />
        </Protected>
      }
    />
    <Route
      path="/task-types/edit/:id"
      element={
        <Protected permission="task-type-edit">
          <CreateTaskType />
        </Protected>
      }
    />
    <Route
      path="/task-types/view/:id"
      element={
        <Protected permission="task-type-view">
          <ViewTaskType />
        </Protected>
      }
    />
    <Route
      path="/task-types"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/task-types"]}>
          <TaskTypeList />
        </Protected>
      }
    />

    {/* Task categories */}
    <Route
      path="/task-categories/create"
      element={
        <Protected permission="task-category-create">
          <CreateTaskCategory />
        </Protected>
      }
    />
    <Route
      path="/task-categories/edit/:id"
      element={
        <Protected permission="task-category-edit">
          <CreateTaskCategory />
        </Protected>
      }
    />
    <Route
      path="/task-categories/view/:id"
      element={
        <Protected permission="task-category-view">
          <ViewTaskCategory />
        </Protected>
      }
    />
    <Route
      path="/task-categories"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/task-categories"]}>
          <TaskCategoryList />
        </Protected>
      }
    />

    {/* Task templates */}
    <Route
      path="/task-templates/create"
      element={
        <Protected permission="task-template-create">
          <CreateTaskTemplate />
        </Protected>
      }
    />
    <Route
      path="/task-templates/edit/:id"
      element={
        <Protected permission="task-template-edit">
          <CreateTaskTemplate />
        </Protected>
      }
    />
    <Route
      path="/task-templates/view/:id"
      element={
        <Protected permission="task-template-view">
          <ViewTaskTemplate />
        </Protected>
      }
    />
    <Route
      path="/task-templates"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/task-templates"]}>
          <TaskTemplateList />
        </Protected>
      }
    />

    {/* Tasks */}
    <Route
      path="/tasks/create"
      element={
        <Protected permission="task-create">
          <CreateTask />
        </Protected>
      }
    />
    <Route
      path="/tasks/edit/:id"
      element={
        <Protected permission="task-edit">
          <CreateTask />
        </Protected>
      }
    />
    <Route
      path="/tasks/view/:id"
      element={
        <Protected permission="task-view">
          <ViewTask />
        </Protected>
      }
    />
    <Route
      path="/tasks"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/tasks"]}>
          <TaskList />
        </Protected>
      }
    />

    {/* Lead quizzes */}
    <Route
      path="/lead-quizzes/create"
      element={
        <Protected permission="lead-quiz-create">
          <CreateLeadQuiz />
        </Protected>
      }
    />
    <Route
      path="/lead-quizzes/edit/:id"
      element={
        <Protected permission="lead-quiz-edit">
          <CreateLeadQuiz />
        </Protected>
      }
    />
    <Route
      path="/lead-quizzes/view/:id"
      element={
        <Protected permission="lead-quiz-view">
          <ViewLeadQuiz />
        </Protected>
      }
    />
    <Route
      path="/lead-quizzes"
      element={
        <Protected permissions={LIST_ROUTE_ANY_PERMISSIONS["/lead-quizzes"]}>
          <LeadQuizList />
        </Protected>
      }
    />

    {/* Daily Summary / Agent Call Reports */}
    <Route
      path="/daily-summary"
      element={
        <Protected permission="agentCallReport-list">
          <DailySummaryList />
        </Protected>
      }
    />
    <Route
      path="/daily-summary/create"
      element={
        <Protected permission="agentCallReport-create">
          <CreateDailySummary />
        </Protected>
      }
    />
    <Route
      path="/daily-summary/edit/:id"
      element={
        <Protected permission="agentCallReport-edit">
          <EditDailySummary />
        </Protected>
      }
    />
    <Route
      path="/daily-summary/view/:id"
      element={
        <Protected permission="agentCallReport-view">
          <ViewDailySummary />
        </Protected>
      }
    />

    {/* Calls Counter Summary */}
    <Route
      path="/calls-counter-summary"
      element={
        <Protected>
          <CallsCounterSummary />
        </Protected>
      }
    />
    {/* Leads Report */}
    <Route
      path="/leads-report"
      element={
        <Protected>
          <LeadsReport />
        </Protected>
      }
    />
    <Route
      path="/reports/generate-sales-information"
      element={
        <Protected>
          <GenerateSalesInformation />
        </Protected>
      }
    />
    <Route
      path="/reports/generate-payment-summary"
      element={
        <Protected>
          <GeneratePaymentSummary />
        </Protected>
      }
    />
    <Route
      path="/reports/clients-as-per-status"
      element={
        <Protected>
          <ClientsAsPerStatus />
        </Protected>
      }
    />
    <Route
      path="/reports/recent-activities"
      element={
        <Protected permission="log-activity-view">
          <RecentActivitiesReport />
        </Protected>
      }
    />

    {/* Other */}
    <Route
      path="/view-log/:id"
      element={
        <Protected permission="log-activity-view">
          <ViewLogsPage />
        </Protected>
      }
    />
    {/* Meetings */}
    <Route
      path="/meetings/calendar"
      element={
        <Protected permission="meeting-list">
          <MeetingCalendar />
        </Protected>
      }
    />
    <Route
      path="/meetings/create"
      element={
        <Protected permission="meeting-create">
          <CreateMeeting />
        </Protected>
      }
    />
    <Route
      path="/meetings/edit/:id"
      element={
        <Protected permission="meeting-edit">
          <EditMeeting />
        </Protected>
      }
    />
    <Route
      path="/meetings/view/:id"
      element={
        <Protected permission="meeting-view">
          <MeetingView />
        </Protected>
      }
    />

    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>
);
